import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, Trash2, Loader2, LogIn, Wrench, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HistoryPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('logons');
  const [logons, setLogons] = useState([]);
  const [tools, setTools] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [l, t, r] = await Promise.all([
        axios.get(`${API}/history/logons`, { headers }),
        axios.get(`${API}/history/tools`, { headers }),
        axios.get(`${API}/history/resources`, { headers }),
      ]);
      setLogons(l.data);
      setTools(t.data);
      setResources(r.data);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleExport = async (type) => {
    try {
      const res = await axios.get(`${API}/history/export/${type}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `history_${type}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${type}`);
    } catch { toast.error('Export failed'); }
  };

  const handleClear = async (type) => {
    const label = type === 'all' ? 'ALL history' : `${type} history`;
    if (!window.confirm(`Clear ${label}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/history/clear/${type}`, { headers });
      toast.success(`${label} cleared`);
      fetchAll();
    } catch { toast.error('Clear failed'); }
  };

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString();
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="history-heading">History</h1>
        <p className="text-slate-600 mt-1">View and manage application activity logs</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="logons" data-testid="tab-logons"><LogIn className="w-4 h-4 mr-1" />Logons</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools"><Wrench className="w-4 h-4 mr-1" />Tools</TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources"><FileText className="w-4 h-4 mr-1" />Resources</TabsTrigger>
        </TabsList>

        {/* Logons Tab */}
        <TabsContent value="logons">
          <Card>
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('logons')} data-testid="export-logons-btn"><Download className="w-3 h-3 mr-1" />Export Logons</Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('all')} data-testid="export-all-btn"><Download className="w-3 h-3 mr-1" />Export All</Button>
              <div className="flex-1" />
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleClear('logons')} data-testid="clear-logons-btn"><Trash2 className="w-3 h-3 mr-1" />Clear Logons</Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleClear('all')} data-testid="clear-all-btn"><Trash2 className="w-3 h-3 mr-1" />Clear All History</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="logons-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Username</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logons.length === 0 ? (
                    <tr><td colSpan={2} className="p-6 text-center text-slate-400">No logon records</td></tr>
                  ) : logons.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-sm text-slate-700">{r.username} ({r.email})</td>
                      <td className="p-3 text-sm text-slate-600">{fmtDate(r.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools">
          <Card>
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('tools')} data-testid="export-tools-btn"><Download className="w-3 h-3 mr-1" />Export Tools</Button>
              <div className="flex-1" />
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleClear('tools')} data-testid="clear-tools-btn"><Trash2 className="w-3 h-3 mr-1" />Clear Tools History</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="tools-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Tool Name</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Username</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  {tools.length === 0 ? (
                    <tr><td colSpan={3} className="p-6 text-center text-slate-400">No tool usage records</td></tr>
                  ) : tools.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-sm font-medium text-slate-900">{r.tool_name}</td>
                      <td className="p-3 text-sm text-slate-700">{r.username}</td>
                      <td className="p-3 text-sm text-slate-600">{fmtDate(r.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources">
          <Card>
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('resources')} data-testid="export-resources-btn"><Download className="w-3 h-3 mr-1" />Export Resources</Button>
              <div className="flex-1" />
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleClear('resources')} data-testid="clear-resources-btn"><Trash2 className="w-3 h-3 mr-1" />Clear Resources History</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="resources-history-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Resource Name</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Username</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  {resources.length === 0 ? (
                    <tr><td colSpan={3} className="p-6 text-center text-slate-400">No resource download records</td></tr>
                  ) : resources.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-sm font-medium text-slate-900">{r.resource_name}</td>
                      <td className="p-3 text-sm text-slate-700">{r.username}</td>
                      <td className="p-3 text-sm text-slate-600">{fmtDate(r.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HistoryPage;
